import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Toaster, toast } from 'sonner';
import { Star, ArrowLeft, CircleNotch, Target } from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';
import { MyPredictionBlock, syncConstraintsToDB } from '../components/MyPrediction';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TEMP_OPTIONS = [
  { value: 'HOT', label: 'Hot+Heating', crossKey: 'hot_heating', cls: 'bg-gradient-to-r from-red-500 to-orange-500 text-white', tag: 'STRONG BUY', tagCls: 'bg-emerald-100 text-emerald-800' },
  { value: 'WARM', label: 'Warm+Cooling', crossKey: 'warm_cooling', cls: 'bg-gradient-to-r from-amber-500 to-blue-500 text-white', tag: 'REVERSION', tagCls: 'bg-amber-100 text-amber-800' },
  { value: 'COLD', label: 'Cold+Heating', crossKey: 'cold_heating', cls: 'bg-gradient-to-r from-blue-500 to-orange-500 text-white', tag: 'EMERGING', tagCls: 'bg-orange-100 text-orange-700' },
  { value: 'HOT_WARM', label: 'Hot+Flat', crossKey: 'hot_flat', cls: 'bg-red-400 text-white', tag: 'HOLD', tagCls: 'bg-gray-100 text-gray-600' },
  { value: 'WARM_COLD', label: 'Hot+Cooling', crossKey: 'hot_cooling', cls: 'bg-gradient-to-r from-red-400 to-blue-400 text-white', tag: 'DECLINING', tagCls: 'bg-blue-100 text-blue-700' },
  { value: 'HOT_COLD', label: 'Warm+Heating', crossKey: 'warm_heating', cls: 'bg-gradient-to-r from-amber-500 to-orange-500 text-white', tag: 'SURPRISE', tagCls: 'bg-orange-100 text-orange-700' },
];
const ALL_POSITIONS = ['ball_1', 'ball_2', 'ball_3', 'ball_4', 'ball_5', 'lucky_star_1', 'lucky_star_2'];
const POS_LABELS = { ball_1: 'B1', ball_2: 'B2', ball_3: 'B3', ball_4: 'B4', ball_5: 'B5', lucky_star_1: 'LS1', lucky_star_2: 'LS2' };

const ballLabels = { ball_1: 'Ball 1', ball_2: 'Ball 2', ball_3: 'Ball 3', ball_4: 'Ball 4', ball_5: 'Ball 5' };
const bkeys = ['ball_1', 'ball_2', 'ball_3', 'ball_4', 'ball_5'];

export default function WindowCrossAnalysis() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [calendarData, setCalendarData] = useState(null);
  const [mainTab, setMainTab] = useState('summary');
  const [tempPrefs, setTempPrefs] = useState({});
  const [cSet, setCSet] = useState(1);
  const [dbSets, setDbSets] = useState({});

  const handleCSetChange = (s) => {
    setCSet(s);
    const saved = dbSets[`C${s}`]?.temp_prefs;
    setTempPrefs(saved || {});
  };

  useEffect(() => {
    (async () => {
      try {
        const [res, calRes, csRes] = await Promise.all([
          axios.get(`${API}/analysis/cross-window-summary`),
          axios.get(`${API}/analysis/calendar-patterns`).catch(() => null),
          axios.get(`${API}/analysis/constraint-sets`),
        ]);
        setData(res.data);
        if (calRes) setCalendarData(calRes.data);
        const cs = csRes.data || {};
        setDbSets(cs);
        setTempPrefs(cs.C1?.temp_prefs || {});
      } catch { toast.error('Failed to fetch'); }
      finally { setLoading(false); }
    })();
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      <Toaster position="top-right" richColors />
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="sm" onClick={() => navigate('/')} data-testid="back-btn"><ArrowLeft size={18} /></Button>
              <div className="w-10 h-10 bg-gradient-to-br from-[#002FA7] to-purple-700 rounded flex items-center justify-center"><Star size={24} weight="fill" className="text-amber-400" /></div>
              <div><h1 className="text-2xl font-bold text-gray-900">W11 vs W21</h1><p className="text-xs text-gray-500">Cross-Analysis: Hot/Cold + Momentum</p></div>
            </div>
          </div>
        </div>
      </header>
      <main className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {loading ? <div className="flex justify-center py-20"><CircleNotch size={32} className="animate-spin text-[#002FA7]" /></div> : data ? (
          <div className="space-y-4">
            <div className="flex gap-2 bg-white border border-gray-200 rounded-lg p-1.5" data-testid="cross-main-tabs">
              {[
                { key: 'w21', label: 'W21: Momentum (Heating/Cooling)' },
                { key: 'w11', label: 'W11: Hot/Cold/Warm' },
                { key: 'summary', label: 'Cross-Analysis Summary' },
                { key: 'myprediction', label: `My Prediction${Object.keys(tempPrefs).length > 0 ? ` (${Object.keys(tempPrefs).length})` : ''}` },
              ].map(t => (
                <button key={t.key} onClick={() => setMainTab(t.key)}
                  className={`flex-1 px-4 py-2.5 rounded-md text-sm font-bold transition-colors ${mainTab === t.key ? (t.key === 'myprediction' ? 'bg-emerald-600 text-white' : 'bg-[#002FA7] text-white') : 'text-gray-600 hover:bg-gray-100'}`}
                  data-testid={`tab-${t.key}`}>
                  {t.label}
                </button>
              ))}
            </div>

            {mainTab === 'w21' && <W21Tab data={data} />}
            {mainTab === 'w11' && <W11Tab data={data} />}
            {mainTab === 'summary' && <SummaryTab data={data} />}
            {mainTab === 'myprediction' && <MyPredictionTab tempPrefs={tempPrefs} setTempPrefs={setTempPrefs} crossData={data} calendarData={calendarData} cSet={cSet} onSetChange={handleCSetChange} dbSets={dbSets} />}
          </div>
        ) : null}
      </main>
    </div>
  );
}

/* ========== W21 Tab: Current Heating/Flat/Cooling ========== */
function W21Tab({ data }) {
  return (
    <div className="space-y-4">
      <div className="bg-gradient-to-r from-amber-600 to-orange-700 rounded-xl p-5 text-white" data-testid="w21-tab-header">
        <h2 className="text-xl font-bold mb-1">Window 21 — Momentum Status</h2>
        <p className="text-sm text-amber-100">Heating/Flat/Cooling based on rolling window of 20 draws. Compares frequency in the older half vs newer half.</p>
      </div>
      {bkeys.map(bk => {
        const s = data.w21_status?.[bk] || {};
        return (
          <div key={bk} className="bg-white border rounded-lg overflow-hidden">
            <div className="bg-gradient-to-r from-amber-600 to-orange-600 text-white px-4 py-2.5 flex items-center justify-between">
              <span className="font-bold">{ballLabels[bk]}</span>
              <div className="flex gap-3 text-[10px]">
                <span className="bg-orange-400/30 px-2 py-0.5 rounded">HEATING: {s.heating?.length || 0}</span>
                <span className="bg-white/20 px-2 py-0.5 rounded">FLAT: {s.flat?.length || 0}</span>
                <span className="bg-blue-400/30 px-2 py-0.5 rounded">COOLING: {s.cooling?.length || 0}</span>
              </div>
            </div>
            <div className="p-3 grid grid-cols-1 md:grid-cols-3 gap-3">
              <NumCircles label="HEATING" nums={s.heating} color="bg-orange-500" labelColor="text-orange-700" borderColor="border-orange-200" bgColor="bg-orange-50" />
              <NumCircles label="FLAT" nums={s.flat} color="bg-gray-400" labelColor="text-gray-600" borderColor="border-gray-200" bgColor="bg-gray-50" />
              <NumCircles label="COOLING" nums={s.cooling} color="bg-blue-500" labelColor="text-blue-700" borderColor="border-blue-200" bgColor="bg-blue-50" />
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* ========== W11 Tab: Current Hot/Warm/Cold ========== */
function W11Tab({ data }) {
  return (
    <div className="space-y-4">
      <div className="bg-gradient-to-r from-red-600 to-rose-700 rounded-xl p-5 text-white" data-testid="w11-tab-header">
        <h2 className="text-xl font-bold mb-1">Window 11 — Hot/Cold Status</h2>
        <p className="text-sm text-red-100">HOT = appeared in last 10 draws at position | WARM = last 20 only | COLD = absent from last 20.</p>
      </div>
      {bkeys.map(bk => {
        const s = data.w11_status?.[bk] || {};
        return (
          <div key={bk} className="bg-white border rounded-lg overflow-hidden">
            <div className="bg-gradient-to-r from-slate-700 to-slate-800 text-white px-4 py-2.5 flex items-center justify-between">
              <span className="font-bold">{ballLabels[bk]}</span>
              <div className="flex gap-3 text-[10px]">
                <span className="bg-red-400/30 px-2 py-0.5 rounded">HOT: {s.hot?.length || 0}</span>
                <span className="bg-amber-400/30 px-2 py-0.5 rounded">WARM: {s.warm?.length || 0}</span>
                <span className="bg-blue-400/30 px-2 py-0.5 rounded">COLD: {s.cold?.length || 0}</span>
              </div>
            </div>
            <div className="p-3 grid grid-cols-1 md:grid-cols-3 gap-3">
              <NumCircles label="HOT — Last 10" nums={s.hot} color="bg-red-500" labelColor="text-red-700" borderColor="border-red-200" bgColor="bg-red-50" />
              <NumCircles label="WARM — Last 20" nums={s.warm} color="bg-amber-500" labelColor="text-amber-700" borderColor="border-amber-200" bgColor="bg-amber-50" />
              <NumCircles label="COLD" nums={s.cold} color="bg-blue-500" labelColor="text-blue-700" borderColor="border-blue-200" bgColor="bg-blue-50" />
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* ========== Summary Tab: Cross-Analysis ========== */
function SummaryTab({ data }) {
  const halves = data.halves || {};
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-700 to-indigo-800 rounded-xl p-6 text-white" data-testid="summary-header">
        <div className="flex items-center gap-2 mb-2"><Target size={24} weight="bold" /><h2 className="text-xl font-bold">Cross-Analysis Summary — W11 vs W21</h2></div>
        <p className="text-sm text-purple-200">When both windows agree (HOT+HEATING or WARM+COOLING), prediction confidence is highest. Numbers in agreement zones are your strongest candidates.</p>
      </div>

      {/* Per-Ball Cross-Reference */}
      {bkeys.map(bk => {
        const c = data.cross?.[bk] || {};
        return (
          <div key={bk} className="bg-white border rounded-lg overflow-hidden" data-testid={`cross-${bk}`}>
            <div className="bg-gradient-to-r from-purple-600 to-indigo-700 text-white px-4 py-3 flex items-center justify-between">
              <span className="font-bold text-lg">{ballLabels[bk]}</span>
              <span className="bg-white/20 px-3 py-1 rounded-full text-xs font-bold">Agreement: {c.agreement_pct || 0}%</span>
            </div>
            <div className="p-4 space-y-3">
              {/* Strong Agreement: HOT + HEATING */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <CrossGroup
                  title="HOT + HEATING"
                  subtitle="Strongest signal — recent AND gaining momentum"
                  nums={c.hot_and_heating}
                  bgColor="bg-gradient-to-r from-red-50 to-orange-50"
                  borderColor="border-red-200"
                  circleColor="bg-gradient-to-br from-red-500 to-orange-500"
                  labelColor="text-red-700"
                  strength="STRONG BUY"
                  strengthColor="bg-emerald-100 text-emerald-800"
                />
                <CrossGroup
                  title="WARM + COOLING"
                  subtitle="Both windows say these are fading — mean reversion candidates"
                  nums={c.warm_and_cooling}
                  bgColor="bg-gradient-to-r from-amber-50 to-blue-50"
                  borderColor="border-amber-200"
                  circleColor="bg-gradient-to-br from-amber-500 to-blue-500"
                  labelColor="text-amber-700"
                  strength="REVERSION"
                  strengthColor="bg-amber-100 text-amber-800"
                />
              </div>

              {/* Mixed Signals */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <CrossGroup
                  title="HOT + FLAT"
                  subtitle="Recent but not gaining — watch closely"
                  nums={c.hot_and_flat}
                  bgColor="bg-red-50/50"
                  borderColor="border-gray-200"
                  circleColor="bg-red-400"
                  labelColor="text-gray-700"
                  strength="HOLD"
                  strengthColor="bg-gray-100 text-gray-600"
                />
                <CrossGroup
                  title="HOT + COOLING"
                  subtitle="Recent but losing steam — declining"
                  nums={c.hot_and_cooling}
                  bgColor="bg-red-50/30"
                  borderColor="border-blue-200"
                  circleColor="bg-red-300"
                  labelColor="text-blue-700"
                  strength="DECLINING"
                  strengthColor="bg-blue-100 text-blue-700"
                />
                <CrossGroup
                  title="COLD + HEATING"
                  subtitle="Absent but gaining — emerging candidates"
                  nums={c.cold_and_heating}
                  bgColor="bg-blue-50/50"
                  borderColor="border-orange-200"
                  circleColor="bg-orange-400"
                  labelColor="text-orange-700"
                  strength="EMERGING"
                  strengthColor="bg-orange-100 text-orange-700"
                />
              </div>

              {/* Remaining */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <CrossGroup
                  title="WARM + HEATING"
                  subtitle="Not recent but gaining — potential surprise"
                  nums={c.warm_and_heating}
                  bgColor="bg-amber-50/30"
                  borderColor="border-orange-200"
                  circleColor="bg-amber-400"
                  labelColor="text-orange-600"
                  strength="SURPRISE"
                  strengthColor="bg-orange-100 text-orange-700"
                  compact
                />
                <CrossGroup
                  title="WARM + FLAT"
                  subtitle="Inactive, no momentum"
                  nums={c.warm_and_flat}
                  bgColor="bg-gray-50"
                  borderColor="border-gray-200"
                  circleColor="bg-gray-400"
                  labelColor="text-gray-500"
                  strength="NEUTRAL"
                  strengthColor="bg-gray-100 text-gray-500"
                  compact
                />
                <CrossGroup
                  title="COLD + COOLING"
                  subtitle="Absent and losing further — avoid"
                  nums={c.cold_and_cooling}
                  bgColor="bg-blue-50/30"
                  borderColor="border-blue-200"
                  circleColor="bg-blue-400"
                  labelColor="text-blue-600"
                  strength="AVOID"
                  strengthColor="bg-red-100 text-red-700"
                  compact
                />
              </div>
            </div>
          </div>
        );
      })}

      {/* First Half vs Second Half: Both Windows Side by Side */}
      <div className="bg-white border rounded-lg overflow-hidden" data-testid="halves-comparison">
        <div className="bg-gradient-to-r from-slate-700 to-slate-800 text-white px-4 py-3">
          <h3 className="font-bold text-lg">First Half (Draws 1-14) vs Second Half (Draws 15-28) — Both Windows</h3>
          <p className="text-xs text-white/60 mt-1">Side-by-side comparison showing how both W11 and W21 classifications shifted between halves</p>
        </div>
        <div className="p-4 overflow-x-auto">
          <table className="w-full text-sm" data-testid="halves-table">
            <thead>
              <tr className="border-b bg-gray-50">
                <th className="text-left py-2 px-3 text-[10px] uppercase text-gray-500" rowSpan={2}>Ball</th>
                <th className="text-center py-1 px-2 text-[10px] uppercase text-orange-600 border-b border-orange-200" colSpan={3}>W21 — 1st Half</th>
                <th className="text-center py-1 px-2 text-[10px] uppercase text-orange-800 border-b border-orange-400" colSpan={3}>W21 — 2nd Half</th>
                <th className="text-center py-1 px-2 text-[10px] uppercase text-red-600 border-b border-red-200" colSpan={3}>W11 — 1st Half</th>
                <th className="text-center py-1 px-2 text-[10px] uppercase text-red-800 border-b border-red-400" colSpan={3}>W11 — 2nd Half</th>
              </tr>
              <tr className="border-b bg-gray-50 text-[9px] text-gray-500">
                <th className="px-2 py-1 text-center">HEAT</th><th className="px-2 py-1 text-center">FLAT</th><th className="px-2 py-1 text-center">COOL</th>
                <th className="px-2 py-1 text-center">HEAT</th><th className="px-2 py-1 text-center">FLAT</th><th className="px-2 py-1 text-center">COOL</th>
                <th className="px-2 py-1 text-center">HOT</th><th className="px-2 py-1 text-center">WARM</th><th className="px-2 py-1 text-center">COLD</th>
                <th className="px-2 py-1 text-center">HOT</th><th className="px-2 py-1 text-center">WARM</th><th className="px-2 py-1 text-center">COLD</th>
              </tr>
            </thead>
            <tbody>
              {bkeys.map(bk => {
                const w21fh = halves.w21_first_half?.[bk] || {};
                const w21sh = halves.w21_second_half?.[bk] || {};
                const w11fh = halves.w11_first_half?.[bk] || {};
                const w11sh = halves.w11_second_half?.[bk] || {};

                const w21HeatDiff = ((w21sh.HEATING?.pct || 0) - (w21fh.HEATING?.pct || 0)).toFixed(1);
                const w21CoolDiff = ((w21sh.COOLING?.pct || 0) - (w21fh.COOLING?.pct || 0)).toFixed(1);
                const w11HotDiff = ((w11sh.HOT?.pct || 0) - (w11fh.HOT?.pct || 0)).toFixed(1);
                const w11WarmDiff = ((w11sh.WARM?.pct || 0) - (w11fh.WARM?.pct || 0)).toFixed(1);

                return (
                  <tr key={bk} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="py-2 px-3 font-bold text-gray-800">{ballLabels[bk]}</td>
                    {/* W21 First Half */}
                    <td className="py-2 px-2 text-center font-mono text-orange-600 font-bold">{w21fh.HEATING?.pct || 0}%</td>
                    <td className="py-2 px-2 text-center font-mono text-gray-500">{w21fh.FLAT?.pct || 0}%</td>
                    <td className="py-2 px-2 text-center font-mono text-blue-600">{w21fh.COOLING?.pct || 0}%</td>
                    {/* W21 Second Half */}
                    <td className="py-2 px-2 text-center">
                      <span className="font-mono font-bold text-orange-600">{w21sh.HEATING?.pct || 0}%</span>
                      <DiffBadge diff={w21HeatDiff} />
                    </td>
                    <td className="py-2 px-2 text-center font-mono text-gray-500">{w21sh.FLAT?.pct || 0}%</td>
                    <td className="py-2 px-2 text-center">
                      <span className="font-mono font-bold text-blue-600">{w21sh.COOLING?.pct || 0}%</span>
                      <DiffBadge diff={w21CoolDiff} invert />
                    </td>
                    {/* W11 First Half */}
                    <td className="py-2 px-2 text-center font-mono text-red-600 font-bold">{w11fh.HOT?.pct || 0}%</td>
                    <td className="py-2 px-2 text-center font-mono text-amber-600">{w11fh.WARM?.pct || 0}%</td>
                    <td className="py-2 px-2 text-center font-mono text-blue-600">{w11fh.COLD?.pct || 0}%</td>
                    {/* W11 Second Half */}
                    <td className="py-2 px-2 text-center">
                      <span className="font-mono font-bold text-red-600">{w11sh.HOT?.pct || 0}%</span>
                      <DiffBadge diff={w11HotDiff} />
                    </td>
                    <td className="py-2 px-2 text-center">
                      <span className="font-mono font-bold text-amber-600">{w11sh.WARM?.pct || 0}%</span>
                      <DiffBadge diff={w11WarmDiff} invert />
                    </td>
                    <td className="py-2 px-2 text-center font-mono text-blue-600">{w11sh.COLD?.pct || 0}%</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Convergence Insight */}
      <div className="bg-gradient-to-r from-emerald-600 to-teal-700 rounded-xl p-5 text-white" data-testid="convergence-insight">
        <div className="flex items-center gap-2 mb-2"><Target size={22} weight="bold" /><h3 className="font-bold text-lg">Convergence Insight</h3></div>
        <p className="text-sm text-emerald-100">
          When W11 (recency-based) and W21 (momentum-based) agree on a number's direction,
          prediction confidence is <strong>significantly higher</strong>.
          Numbers that are both <strong>HOT + HEATING</strong> are your strongest picks.
          Numbers where <strong>WARM aligns with COOLING</strong> (both declining) indicate
          mean-reversion candidates — they were recently active but are losing steam, meaning
          their slots are opening up for other numbers.
        </p>
        <p className="text-sm text-emerald-200 mt-2">
          <strong>Strategy:</strong> Prioritize HOT+HEATING numbers for each ball.
          For contrarian picks, look at COLD+HEATING (emerging) numbers that both windows identify as due.
        </p>
      </div>
    </div>
  );
}

/* ========== Shared Components ========== */
function NumCircles({ label, nums, color, labelColor, borderColor, bgColor }) {
  return (
    <div className={`rounded-lg border ${borderColor} ${bgColor} p-2.5`}>
      <div className={`text-[10px] font-bold ${labelColor} uppercase mb-2`}>{label} ({(nums || []).length})</div>
      <div className="flex flex-wrap gap-1.5">
        {(nums || []).map(n => (
          <span key={n} className={`inline-flex w-8 h-8 rounded-full ${color} text-white items-center justify-center font-mono font-bold text-xs`}>{n}</span>
        ))}
        {(!nums || nums.length === 0) && <span className="text-[10px] text-gray-400">None</span>}
      </div>
    </div>
  );
}

function CrossGroup({ title, subtitle, nums, bgColor, borderColor, circleColor, labelColor, strength, strengthColor, compact }) {
  return (
    <div className={`rounded-lg border ${borderColor} ${bgColor} p-3`}>
      <div className="flex items-center justify-between mb-1">
        <div className={`text-xs font-bold ${labelColor} uppercase`}>{title} ({(nums || []).length})</div>
        <span className={`text-[9px] font-bold px-2 py-0.5 rounded-full ${strengthColor}`}>{strength}</span>
      </div>
      {!compact && <p className="text-[10px] text-gray-500 mb-2">{subtitle}</p>}
      <div className="flex flex-wrap gap-1.5">
        {(nums || []).map(n => (
          <span key={n} className={`inline-flex w-8 h-8 rounded-full ${circleColor} text-white items-center justify-center font-mono font-bold text-xs`}>{n}</span>
        ))}
        {(!nums || nums.length === 0) && <span className="text-[10px] text-gray-400">None</span>}
      </div>
    </div>
  );
}

function DiffBadge({ diff, invert }) {
  const val = parseFloat(diff);
  const positive = invert ? val < 0 : val > 0;
  const negative = invert ? val > 0 : val < 0;
  if (val === 0) return null;
  return (
    <span className={`ml-1 text-[9px] font-bold ${positive ? 'text-emerald-600' : negative ? 'text-red-500' : 'text-gray-400'}`}>
      {val > 0 ? '+' : ''}{diff}
    </span>
  );
}


function MyPredictionTab({ tempPrefs, setTempPrefs, crossData, calendarData, cSet, onSetChange, dbSets }) {
  // tempPrefs: { ball_1: "HOT,COLD", ball_2: "WARM" } — comma-separated for multi-select
  // numberPicks: { ball_2: [2,6], ... } — numbers added directly from cal row
  const [numberPicks, setNumberPicks] = React.useState(() => dbSets?.[`C${cSet}`]?.number_picks || {});

  // Sync numberPicks when set changes
  const prevCSet = React.useRef(cSet);
  React.useEffect(() => {
    if (prevCSet.current !== cSet) {
      setNumberPicks(dbSets?.[`C${cSet}`]?.number_picks || {});
      prevCSet.current = cSet;
    }
  }, [cSet, dbSets]);

  const addNumberPick = (pos, num) => {
    if ((numberPicks[pos] || []).includes(num)) return; // already added, do nothing
    setNumberPicks(prev => {
      const next = { ...prev };
      next[pos] = [...(next[pos] || []), num].sort((a, b) => a - b);
      syncConstraintsToDB(cSet, { number_picks: Object.keys(next).length > 0 ? next : null });
      return next;
    });
    toast.success(`${POS_LABELS[pos]}: Added #${num} to W6 picks`);
  };

  const removeNumberPick = (pos, num) => {
    if (!(numberPicks[pos] || []).includes(num)) return;
    setNumberPicks(prev => {
      const next = { ...prev };
      next[pos] = (next[pos] || []).filter(n => n !== num);
      if (next[pos].length === 0) delete next[pos];
      syncConstraintsToDB(cSet, { number_picks: Object.keys(next).length > 0 ? next : null });
      return next;
    });
    toast.info(`${POS_LABELS[pos]}: Removed #${num} from W6 picks`);
  };

  const getSelected = (pos) => {
    const val = tempPrefs[pos];
    if (!val) return new Set();
    return new Set(val.split(',').filter(Boolean));
  };

  const handleToggle = (pos, val) => {
    setTempPrefs(prev => {
      const next = { ...prev };
      const selected = new Set((next[pos] || '').split(',').filter(Boolean));
      if (selected.has(val)) return prev; // already selected, don't toggle off
      selected.add(val);
      next[pos] = [...selected].join(',');
      return next;
    });
  };

  const removeZone = (pos, val) => {
    setTempPrefs(prev => {
      const next = { ...prev };
      const selected = new Set((next[pos] || '').split(',').filter(Boolean));
      selected.delete(val);
      if (selected.size === 0) { delete next[pos]; } else { next[pos] = [...selected].join(','); }
      return next;
    });
  };

  const clearPos = (pos) => {
    setTempPrefs(prev => { const next = { ...prev }; delete next[pos]; return next; });
  };

  const handleSave = () => {
    syncConstraintsToDB(cSet, { temp_prefs: tempPrefs });
  };

  const handleClear = () => {
    setTempPrefs({});
    syncConstraintsToDB(cSet, { temp_prefs: null });
  };

  const displayVal = Object.keys(tempPrefs).length > 0
    ? ALL_POSITIONS.filter(p => tempPrefs[p]).map(p => {
        const labels = tempPrefs[p].split(',').map(v => TEMP_OPTIONS.find(o => o.value === v)?.label || v).join('+');
        return `${POS_LABELS[p]}=${labels}`;
      }).join(', ')
    : null;

  // Get cross-referenced agreement zones from cross-analysis data
  const getCrossNums = (pos) => {
    const c = crossData?.cross?.[pos] || {};
    return {
      hot_heating: c.hot_and_heating || [],
      warm_cooling: c.warm_and_cooling || [],
      cold_heating: c.cold_and_heating || [],
      hot_flat: c.hot_and_flat || [],
      hot_cooling: c.hot_and_cooling || [],
      warm_heating: c.warm_and_heating || [],
      agreement: c.agreement_pct || 0,
    };
  };

  // Compute selected numbers total for a position
  const getSelectedNums = (pos, crossNums) => {
    const selected = getSelected(pos);
    if (selected.size === 0 || !crossNums) return [];
    const nums = new Set();
    selected.forEach(val => {
      const opt = TEMP_OPTIONS.find(o => o.value === val);
      if (opt && crossNums[opt.crossKey]) {
        crossNums[opt.crossKey].forEach(n => nums.add(n));
      }
    });
    return [...nums].sort((a, b) => a - b);
  };

  // Map position keys to calendar data keys
  const calKey = (pos) => {
    if (pos === 'lucky_star_1') return 'ls_1';
    if (pos === 'lucky_star_2') return 'ls_2';
    return pos;
  };

  // Minimum valid number per ball position (sorted ascending: B1≥1, B2≥2, etc.)
  const minForPos = { ball_1: 1, ball_2: 2, ball_3: 3, ball_4: 4, ball_5: 5, lucky_star_1: 1, lucky_star_2: 1 };

  // Get calendar date numbers per position (own calendar only)
  const getOwnCalendarNums = (pos) => {
    const pa = calendarData?.positional_analysis?.[calKey(pos)];
    if (!pa?.values) return [];
    return [...new Set(pa.values.map(v => v.value))].sort((a, b) => a - b);
  };

  // LS combined calendar numbers (LS1 + LS2 unique)
  const lsCalNums = [...new Set([...getOwnCalendarNums('lucky_star_1'), ...getOwnCalendarNums('lucky_star_2')])].sort((a, b) => a - b);

  // Get merged calendar numbers for a ball position: own cal + LS cal (filtered by range, excluding already-in-zone numbers)
  const getCalendarNums = (pos) => {
    const own = getOwnCalendarNums(pos);
    if (!pos.startsWith('ball_')) return own; // LS positions: just own calendar
    const minVal = minForPos[pos] || 1;
    const lsFiltered = lsCalNums.filter(n => n >= minVal);
    return [...new Set([...own, ...lsFiltered])].sort((a, b) => a - b);
  };

  // Track which numbers are from LS (not the ball's own calendar)
  const isLsOrigin = (pos, num) => {
    if (!pos.startsWith('ball_')) return false;
    const own = getOwnCalendarNums(pos);
    return !own.includes(num) && lsCalNums.includes(num);
  };

  // Check which calendar numbers are in which zone (per-position, for button highlighting)
  const getCalendarZoneMap = (pos, crossNums) => {
    const calNums = getCalendarNums(pos);
    if (!calNums.length || !crossNums) return { found: {}, missing: [], all: calNums };
    const found = {};
    TEMP_OPTIONS.forEach(opt => {
      const zoneNums = crossNums[opt.crossKey] || [];
      const inZone = calNums.filter(n => zoneNums.includes(n));
      if (inZone.length > 0) found[opt.crossKey] = inZone;
    });
    return { found, all: calNums };
  };

  // CUMULATIVE: Collect all calendar numbers and all zone numbers across all positions (B1-B5 + LS1, LS2)
  const allCalNums = [...new Set(ALL_POSITIONS.flatMap(p => getCalendarNums(p)))].sort((a, b) => a - b);
  const allZoneNumsCumulative = new Set();
  const calNumInZone = {}; // number -> [positions where it appears in a zone]
  ALL_POSITIONS.forEach(pk => {
    const cn = getCrossNums(pk);
    TEMP_OPTIONS.forEach(opt => {
      (cn[opt.crossKey] || []).forEach(n => {
        allZoneNumsCumulative.add(n);
        if (allCalNums.includes(n)) {
          if (!calNumInZone[n]) calNumInZone[n] = [];
          calNumInZone[n].push(pk);
        }
      });
    });
  });
  const globalMissingCal = allCalNums.filter(n => !allZoneNumsCumulative.has(n));
  const globalCoveredCal = allCalNums.filter(n => allZoneNumsCumulative.has(n));

  return (
    <div className="space-y-4">
      <MyPredictionBlock
        storageKey="em_w11_temp_prefs"
        label="Temperature Preference"
        description="Select cross-referenced W11×W21 agreement zones per ball. Multi-select allowed."
        value={displayVal}
        onSave={handleSave}
        onClear={handleClear}
        activeSet={cSet}
        onSetChange={onSetChange}
        appliedSets={[1,2,3,4,5].filter(n => {
          const tp = dbSets?.[`C${n}`]?.temp_prefs;
          return tp && Object.keys(tp).length > 0;
        })}
      >
        <div className="text-xs text-white/60">Select zones below, then click Save</div>
      </MyPredictionBlock>

      {/* CUMULATIVE Calendar Date Numbers Banner */}
      {allCalNums.length > 0 && (
        <div className="rounded-lg p-3 bg-indigo-50 border border-indigo-200" data-testid="calendar-cumulative">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[10px] font-bold text-indigo-800 uppercase">Calendar Date Numbers ({calendarData?.day}/{calendarData?.month}) — All Positions Cumulative</span>
            <span className="text-[10px] font-bold text-indigo-600">{allCalNums.length} unique numbers across B1-B5 + LS1-LS2</span>
          </div>
          <div className="flex flex-wrap gap-1.5 mb-1.5">
            {allCalNums.map(n => {
              const isMissing = globalMissingCal.includes(n);
              const positions = calNumInZone[n] || [];
              return (
                <div key={n} className="flex flex-col items-center gap-0.5">
                  <span className={`inline-flex items-center justify-center w-9 h-9 rounded-full font-bold text-sm font-mono ${
                    isMissing ? 'bg-red-500 text-white ring-2 ring-red-300' : 'bg-emerald-500 text-white ring-2 ring-emerald-300'
                  }`} title={isMissing ? `#${n} not in any ball's zones` : `#${n} in ${positions.map(p => POS_LABELS[p]).join(', ')}`}>
                    {n}
                  </span>
                  {!isMissing && <span className="text-[8px] text-emerald-700 font-bold">{positions.map(p => POS_LABELS[p]).join(',')}</span>}
                </div>
              );
            })}
          </div>
          {globalMissingCal.length > 0 ? (
            <div className="mt-2 rounded-lg border border-red-200 bg-red-50 p-2.5" data-testid="missing-cal-detail">
              <div className="flex items-center gap-1.5 mb-2">
                <span className="text-[10px] font-bold text-red-700 bg-red-100 px-1.5 py-0.5 rounded">NOT IN ANY POSITION'S ZONES</span>
                <span className="text-[9px] text-red-500">— add via W6 Number Picks for target positions</span>
              </div>
              <div className="space-y-1.5">
                {globalMissingCal.map(n => {
                  // Find which positions this number appeared in historically on this date
                  const posHistory = [];
                  ALL_POSITIONS.forEach(pk => {
                    const vals = calendarData?.positional_analysis?.[calKey(pk)]?.values || [];
                    const matches = vals.filter(v => v.value === n);
                    if (matches.length > 0) {
                      posHistory.push({ ball: pk, years: matches.map(m => m.year) });
                    }
                  });
                  return (
                    <div key={n} className="flex items-center gap-3 bg-white rounded-lg p-2 border border-red-200">
                      <span className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-red-500 text-white font-bold text-sm font-mono ring-2 ring-red-300">{n}</span>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          {posHistory.map(ph => (
                            <div key={ph.ball} className="flex items-center gap-1">
                              <span className="text-[10px] font-bold text-[#002FA7] bg-blue-100 px-1.5 py-0.5 rounded">{POS_LABELS[ph.ball]}</span>
                              <span className="text-[10px] font-mono text-gray-600">{ph.years.join(', ')}</span>
                            </div>
                          ))}
                        </div>
                        <p className="text-[9px] text-gray-500 mt-0.5">
                          Appeared {posHistory.reduce((s, ph) => s + ph.years.length, 0)}x on {calendarData?.day}/{calendarData?.month}
                          {posHistory.length > 0 && <span className="font-bold text-red-600 ml-1">— suggest adding to {posHistory.map(p => POS_LABELS[p.ball]).join(', ')} via W6</span>}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : (
            <div className="text-[9px] text-emerald-700 font-bold mt-0.5">All calendar numbers covered across position zones</div>
          )}
          <div className="flex items-center gap-3 mt-1.5 text-[9px] text-gray-500">
            <span><span className="inline-block w-3 h-3 rounded-full bg-emerald-500 align-middle mr-0.5"></span> In zone</span>
            <span><span className="inline-block w-3 h-3 rounded-full bg-red-500 align-middle mr-0.5"></span> Not in any zone</span>
            <span className="text-gray-400">|</span>
            <span>Covered: {globalCoveredCal.length}/{allCalNums.length}</span>
          </div>
        </div>
      )}

      <div className="space-y-3" data-testid="w11-temp-selectors">
        {ALL_POSITIONS.map(pos => {
          const isBall = pos.startsWith('ball_');
          const crossNums = getCrossNums(pos);
          const selected = getSelected(pos);
          const selectedNums = getSelectedNums(pos, crossNums);
          const calMap = getCalendarZoneMap(pos, crossNums);
          const calSet = new Set(calMap.all || []);
          return (
            <div key={pos} className={`bg-white border rounded-lg p-4 ${selected.size > 0 ? 'ring-2 ring-emerald-400 border-emerald-300' : 'border-gray-200'}`} data-testid={`temp-selector-${pos}`}>
              {/* Per-position calendar nums inline — clickable to auto-select zone */}
              {calMap.all?.length > 0 && (() => {
                // Build map: number -> zone key that contains it
                const numToZone = {};
                TEMP_OPTIONS.forEach(opt => {
                  (crossNums?.[opt.crossKey] || []).forEach(n => {
                    if (!numToZone[n]) numToZone[n] = [];
                    numToZone[n].push(opt.value);
                  });
                });
                const thisPosAllZoneNums = new Set(TEMP_OPTIONS.flatMap(opt => crossNums?.[opt.crossKey] || []));
                // For display: own cal nums + LS nums NOT already in zones
                const ownCal = getOwnCalendarNums(pos);
                const displayNums = isBall
                  ? calMap.all.filter(n => ownCal.includes(n) || !thisPosAllZoneNums.has(n))
                  : calMap.all;
                const missingFromZones = displayNums.filter(n => !thisPosAllZoneNums.has(n));

                return (
                  <div className="mb-2 space-y-1.5">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-[9px] font-bold text-indigo-700">Cal ({calendarData?.day}/{calendarData?.month}):</span>
                      {displayNums.map(n => {
                        const inZone = thisPosAllZoneNums.has(n);
                        const zones = numToZone[n] || [];
                        const fromLS = isLsOrigin(pos, n);
                        const isPicked = (numberPicks[pos] || []).includes(n);
                        return (
                          <span key={n} className="relative inline-flex">
                            <button
                              onClick={() => {
                                if (inZone && zones.length > 0) {
                                  zones.forEach(z => {
                                    if (!getSelected(pos).has(z)) handleToggle(pos, z);
                                  });
                                } else if (!isPicked) {
                                  addNumberPick(pos, n);
                                }
                              }}
                              className={`inline-flex items-center justify-center w-7 h-7 rounded-full font-bold text-[10px] font-mono transition-all ${
                                inZone
                                  ? selected.size > 0 && zones.some(z => selected.has(z))
                                    ? 'bg-emerald-600 text-white ring-2 ring-emerald-300 shadow-md'
                                    : 'bg-emerald-400 text-white cursor-pointer hover:scale-110 hover:ring-2 hover:ring-emerald-300'
                                  : isPicked
                                    ? 'bg-blue-600 text-white ring-2 ring-blue-300 shadow-md'
                                    : `cursor-pointer hover:scale-110 ${fromLS
                                        ? 'bg-amber-100 text-amber-700 ring-1 ring-amber-300 hover:bg-amber-200'
                                        : 'bg-red-100 text-red-600 ring-1 ring-red-300 hover:bg-red-200'}`
                              }`}
                              title={inZone
                                ? `In zone: ${zones.map(z => TEMP_OPTIONS.find(o => o.value === z)?.label).join(', ')}${selected.size > 0 && zones.some(z => selected.has(z)) ? '' : ' — click to select'}`
                                : isPicked
                                  ? `Added to W6 picks`
                                  : `Click to add #${n} to W6 picks for ${POS_LABELS[pos]}`}
                              data-testid={`cal-num-${pos}-${n}`}
                            >
                              {n}
                            </button>
                            {isPicked && !inZone && (
                              <button onClick={() => removeNumberPick(pos, n)}
                                className="absolute -top-1 -right-1 w-3.5 h-3.5 bg-red-500 text-white rounded-full text-[8px] font-bold flex items-center justify-center hover:bg-red-600 transition-colors"
                                title="Remove from picks"
                              >&times;</button>
                            )}
                          </span>
                        );
                      })}
                      {isBall && displayNums.some(n => isLsOrigin(pos, n)) && (
                        <span className="text-[8px] text-amber-600 font-bold ml-1">+LS</span>
                      )}
                    </div>
                    {missingFromZones.length > 0 && (
                      <div className="flex items-center gap-2 flex-wrap bg-red-50 border border-red-200 rounded-lg px-3 py-1.5" data-testid={`missing-cal-${pos}`}>
                        <span className="text-[9px] font-bold text-red-700 bg-red-100 px-1.5 py-0.5 rounded">NOT IN {POS_LABELS[pos]} ZONES:</span>
                        {missingFromZones.map(n => {
                          const fromLS = isLsOrigin(pos, n);
                          const isPicked = (numberPicks[pos] || []).includes(n);
                          return (
                            <span key={n} className="relative inline-flex">
                              <button onClick={() => { if (!isPicked) addNumberPick(pos, n); }}
                                className={`inline-flex items-center justify-center w-7 h-7 rounded-full font-bold text-[10px] font-mono ring-1 transition-all ${
                                  isPicked
                                    ? 'bg-blue-600 text-white ring-blue-300 ring-2 shadow-md'
                                    : `cursor-pointer hover:scale-110 ${fromLS ? 'bg-amber-500 text-white ring-amber-300 hover:ring-2' : 'bg-red-500 text-white ring-red-300 hover:ring-2'}`
                                }`}
                                title={isPicked ? `Added to W6 picks` : `Click to add #${n} to W6 picks`}
                              >{n}</button>
                              {isPicked && (
                                <button onClick={() => removeNumberPick(pos, n)}
                                  className="absolute -top-1 -right-1 w-3.5 h-3.5 bg-red-500 text-white rounded-full text-[8px] font-bold flex items-center justify-center hover:bg-red-600 transition-colors"
                                  title="Remove from picks"
                                  data-testid={`remove-pick-${pos}-${n}`}
                                >&times;</button>
                              )}
                            </span>
                          );
                        })}
                        <span className="text-[8px] text-red-500 italic ml-1">click to add to W6 picks</span>
                      </div>
                    )}
                  </div>
                );
              })()}

              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  <span className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm ${isBall ? 'bg-[#002FA7] text-white' : 'bg-amber-500 text-white'}`}>
                    {POS_LABELS[pos]}
                  </span>
                  <div>
                    <div className="font-bold text-gray-900 text-sm">{isBall ? `Ball ${pos.split('_')[1]}` : `Lucky Star ${pos.split('_')[2]}`}</div>
                    {selected.size > 0 && (
                      <span className="text-[10px] font-bold text-emerald-600">
                        {selected.size} zone{selected.size > 1 ? 's' : ''} selected — {selectedNums.length} numbers
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {crossNums && <span className="text-[10px] font-bold text-purple-600 bg-purple-50 px-2 py-0.5 rounded">Agreement: {crossNums.agreement}%</span>}
                  {selected.size > 0 && (
                    <button onClick={() => clearPos(pos)} className="text-xs text-gray-400 hover:text-red-500 transition-colors" data-testid={`clear-${pos}`}>Clear</button>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-3 md:grid-cols-6 gap-2 mb-3">
                {TEMP_OPTIONS.map(opt => {
                  const isActive = selected.has(opt.value);
                  const nums = crossNums ? (crossNums[opt.crossKey] || []) : [];
                  const calInZone = calMap.found[opt.crossKey] || [];
                  const calInZoneSet = new Set(calInZone);
                  if (nums.length === 0) return (
                    <div key={opt.value} className="px-3 py-2 rounded-lg text-xs bg-gray-50 text-gray-300 text-center border border-dashed border-gray-200" data-testid={`btn-${pos}-${opt.value}`}>
                      <span>{opt.label}</span>
                      <span className="block text-[8px] mt-0.5 font-bold">{opt.tag}</span>
                      <span className="block text-[9px] mt-1 font-mono">0 nums</span>
                    </div>
                  );
                  return (
                    <div key={opt.value} className="relative">
                      <button
                        onClick={() => handleToggle(pos, opt.value)}
                        className={`w-full px-3 py-2 rounded-lg text-xs font-bold transition-all text-center ${
                          isActive
                            ? `${opt.cls} ring-2 ring-offset-1 ring-gray-900 scale-[1.03] shadow-lg`
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                        data-testid={`btn-${pos}-${opt.value}`}
                      >
                        <span>{opt.label}</span>
                        <span className={`block text-[8px] mt-0.5 font-bold ${isActive ? 'text-white/80' : 'text-gray-400'}`}>{opt.tag}</span>
                        <span className={`block text-[9px] mt-1 font-mono ${isActive ? 'text-white/90' : 'text-gray-500'}`}>
                          {nums.length} num{nums.length !== 1 ? 's' : ''}
                        </span>
                      {nums.length > 0 && (
                        <span className="flex flex-wrap gap-0.5 justify-center mt-0.5">
                          {nums.slice(0, 8).map(n => (
                            <span key={n} className={`inline-block text-[8px] font-mono px-1 rounded ${
                              calInZoneSet.has(n)
                                ? 'bg-emerald-400 text-white font-bold ring-1 ring-emerald-300'
                                : isActive ? 'text-white/70' : 'text-gray-400'
                            }`}>{n}</span>
                          ))}
                          {nums.length > 8 && <span className={`text-[8px] ${isActive ? 'text-white/50' : 'text-gray-300'}`}>+{nums.length - 8}</span>}
                        </span>
                      )}
                      {calInZone.length > 0 && (
                        <span className={`block text-[8px] mt-0.5 font-bold ${isActive ? 'text-emerald-200' : 'text-emerald-600'}`}>
                          {calInZone.length} cal
                        </span>
                      )}
                      </button>
                      {isActive && (
                        <button onClick={(e) => { e.stopPropagation(); removeZone(pos, opt.value); }}
                          className="absolute -top-1.5 -right-1.5 w-4 h-4 bg-red-500 text-white rounded-full text-[9px] font-bold flex items-center justify-center hover:bg-red-600 transition-colors z-10 shadow"
                          title={`Remove ${opt.label}`}
                          data-testid={`remove-zone-${pos}-${opt.value}`}
                        >&times;</button>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Selected numbers summary */}
              {selected.size > 0 && selectedNums.length > 0 && (
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-2.5 mt-2" data-testid={`selected-nums-${pos}`}>
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-[10px] font-bold text-emerald-800">Selected Pool — {selectedNums.length} numbers</span>
                    <span className="text-[9px] text-emerald-600 font-mono">{[...selected].map(v => TEMP_OPTIONS.find(o => o.value === v)?.label).join(' + ')}</span>
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {selectedNums.map(n => (
                      <span key={n} className={`inline-flex items-center justify-center w-7 h-7 rounded-full text-[10px] font-bold font-mono ${
                        calSet.has(n)
                          ? 'bg-emerald-600 text-white ring-2 ring-emerald-400 shadow-md'
                          : 'bg-emerald-500 text-white'
                      }`} title={calSet.has(n) ? `Calendar date number` : ''}>{n}</span>
                    ))}
                  </div>
                  {calMap.all?.length > 0 && (
                    <div className="mt-1.5 text-[9px] text-emerald-700">
                      Calendar nums in pool: {calMap.all.filter(n => selectedNums.includes(n)).length}/{calMap.all.length}
                      {calMap.all.filter(n => selectedNums.includes(n)).length < calMap.all.length && (
                        <span className="text-red-600 ml-1 font-bold">
                          Missing: {calMap.all.filter(n => !selectedNums.includes(n)).join(', ')}
                        </span>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Cross-reference zones with numbers */}
              {crossNums && (
                <div className="space-y-1 border-t border-gray-100 pt-2 mt-2">
                  <div className="flex gap-3 text-[10px] flex-wrap">
                    {crossNums.hot_heating.length > 0 && (
                      <div className="flex items-center gap-1">
                        <span className="font-bold text-red-600">Hot+Heating({crossNums.hot_heating.length}):</span>
                        <span className="font-mono text-gray-700">{crossNums.hot_heating.join(', ')}</span>
                      </div>
                    )}
                    {crossNums.warm_cooling.length > 0 && (
                      <div className="flex items-center gap-1">
                        <span className="font-bold text-amber-600">Warm+Cooling({crossNums.warm_cooling.length}):</span>
                        <span className="font-mono text-gray-700">{crossNums.warm_cooling.join(', ')}</span>
                      </div>
                    )}
                    {crossNums.cold_heating.length > 0 && (
                      <div className="flex items-center gap-1">
                        <span className="font-bold text-orange-600">Cold+Heating({crossNums.cold_heating.length}):</span>
                        <span className="font-mono text-gray-700">{crossNums.cold_heating.join(', ')}</span>
                      </div>
                    )}
                  </div>
                  <div className="flex gap-3 text-[10px] flex-wrap">
                    {crossNums.hot_flat.length > 0 && (
                      <div className="flex items-center gap-1">
                        <span className="font-bold text-gray-500">Hot+Flat({crossNums.hot_flat.length}):</span>
                        <span className="font-mono text-gray-600">{crossNums.hot_flat.join(', ')}</span>
                      </div>
                    )}
                    {crossNums.hot_cooling.length > 0 && (
                      <div className="flex items-center gap-1">
                        <span className="font-bold text-blue-600">Hot+Cooling({crossNums.hot_cooling.length}):</span>
                        <span className="font-mono text-gray-600">{crossNums.hot_cooling.join(', ')}</span>
                      </div>
                    )}
                    {crossNums.warm_heating.length > 0 && (
                      <div className="flex items-center gap-1">
                        <span className="font-bold text-orange-500">Warm+Heating({crossNums.warm_heating.length}):</span>
                        <span className="font-mono text-gray-600">{crossNums.warm_heating.join(', ')}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
